import { ITEM_CATALOG } from './itemCatalog.js';
import { getItemActiveHandler } from './itemActiveRegistry.js';
import './itemActives.js';
import { decrementStackOrClear, stackSellRefund } from './consumableStacks.js';
import {
  applyActiveCooldown,
  getActiveCooldownRemaining,
  isActiveReady
} from './itemActiveCooldown.js';
import { resolveBuy } from './inventoryBuy.js';
import {
  applyUndoLastBuy,
  shouldClearPurchaseOnSell
} from './shopPurchaseUndo.js';

export class InventorySystem {
  constructor(ownerEntity, maxSlots = 6) {
    this.owner = ownerEntity;
    this.maxSlots = maxSlots;
    this.slots = new Array(maxSlots).fill(null);
    this.lastPurchase = null;
  }

  getItemCraftingCost(itemId) {
    const itemDef = ITEM_CATALOG[itemId];
    if (!itemDef) return 0;
    if (!itemDef.components || itemDef.components.length === 0) return itemDef.cost;

    let currentCost = itemDef.cost;
    const tempSlots = [...this.slots];
    itemDef.components.forEach((compKey) => {
      const idx = tempSlots.findIndex((s) => s && s.id === compKey);
      if (idx === -1) return;
      const compDef = ITEM_CATALOG[compKey];
      if (!compDef) return;
      currentCost -= compDef.cost;
      tempSlots[idx] = null;
    });
    return Math.max(0, currentCost);
  }

  buyItem(itemId) {
    return resolveBuy(this, itemId);
  }

  undoLastBuy() {
    const result = applyUndoLastBuy(this, this.lastPurchase);
    if (result.success) this.lastPurchase = null;
    return result;
  }

  sellItem(slotIndex) {
    if (slotIndex < 0 || slotIndex >= this.maxSlots || !this.slots[slotIndex]) {
      return { success: false, reason: 'Casilla vacía' };
    }
    const item = this.slots[slotIndex];
    const refundGold = stackSellRefund(item);
    this.slots[slotIndex] = null;
    this.owner.gold += refundGold;
    if (shouldClearPurchaseOnSell(this.lastPurchase, slotIndex)) {
      this.lastPurchase = null;
    }
    this.recalculateStats();
    return { success: true, refundGold, item };
  }

  getActiveCooldownRemaining(slotIndex, now = 0) {
    return getActiveCooldownRemaining(this.slots[slotIndex], now);
  }

  useItem(slotIndex, options = {}) {
    const item = this.slots[slotIndex];
    if (!item || this.owner.isDead) return false;
    const handler = getItemActiveHandler(item.id);
    if (!handler) return false;
    const now = options.now ?? 0;
    if (!isActiveReady(item, now)) return false;
    const result = handler({ owner: this.owner, item, inventory: this, slotIndex, options });
    if (!result || !result.success) return false;
    if (result.consume) {
      decrementStackOrClear(this.slots, slotIndex);
      if (shouldClearPurchaseOnSell(this.lastPurchase, slotIndex)) {
        this.lastPurchase = null;
      }
    } else if (result.cooldownMs > 0) {
      applyActiveCooldown(item, now, result.cooldownMs);
    }
    this.recalculateStats();
    return true;
  }

  recalculateStats() {
    if (!this.owner) return;
    let bonusAd = 0;
    let bonusMs = 0;
    let bonusAsPct = 0;
    let bonusHp = 0;
    let bonusCritChance = 0;
    this.slots.forEach((item) => {
      if (!item || !item.stats) return;
      if (item.stats.ad) bonusAd += item.stats.ad;
      if (item.stats.ms) bonusMs += item.stats.ms;
      if (item.stats.as) bonusAsPct += item.stats.as;
      if (item.stats.maxHp) bonusHp += item.stats.maxHp;
      if (item.stats.critChance) bonusCritChance += item.stats.critChance;
    });
    const baseAd = 59 + (this.owner.level - 1) * 3.5;
    const baseMs = 325;
    const baseAs = 0.658;
    const baseMaxHp = 570 + (this.owner.level - 1) * 85;
    const rune = this.owner.runeStats || {};
    this.owner.ad = baseAd + bonusAd + (rune.ad || 0);
    this.owner.ms = (baseMs + bonusMs) * (1 + (rune.msPercent || 0));
    this.owner.as = baseAs * (1 + bonusAsPct);
    this.owner.critChance = Math.min(1.0, bonusCritChance);
    const oldMaxHp = this.owner.maxHp;
    this.owner.maxHp = baseMaxHp + bonusHp
      + ((rune.hpPerLevel || 0) * Math.max(0, this.owner.level - 1));
    if (this.owner.maxHp > oldMaxHp) {
      this.owner.hp += this.owner.maxHp - oldMaxHp;
    }
  }
}
